import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { socialAPI } from '../api/social';
import { Send, Calendar, History, Twitter, Facebook, Linkedin, Instagram, Loader2, Trash2, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const SocialMediaManager = () => {
  const [loading, setLoading] = useState(false);
  const [platforms, setPlatforms] = useState({
    twitter: false,
    facebook: false,
    linkedin: false,
    instagram: false
  });
  const [platformStatus, setPlatformStatus] = useState({});
  const [postHistory, setPostHistory] = useState([]);
  const [scheduledPosts, setScheduledPosts] = useState([]);
  
  // Post Form State
  const [postForm, setPostForm] = useState({
    content: '',
    link: '',
    media_url: '',
    schedule_time: ''
  });
  
  useEffect(() => {
    loadPlatformStatus();
    loadPostHistory();
    loadScheduledPosts();
  }, []);
  
  const loadPlatformStatus = async () => {
    try {
      const response = await socialAPI.getPlatformStatus();
      setPlatformStatus(response.data.platforms);
    } catch (error) {
      console.error('Error loading platform status:', error);
    }
  };
  
  const loadPostHistory = async () => {
    try {
      const response = await socialAPI.getPostHistory({ limit: 20 });
      setPostHistory(response.data.posts || []);
    } catch (error) {
      console.error('Error loading post history:', error);
    }
  };
  
  const loadScheduledPosts = async () => {
    try {
      const response = await socialAPI.getScheduledPosts({ limit: 20 });
      setScheduledPosts(response.data.scheduled_posts || []);
    } catch (error) {
      console.error('Error loading scheduled posts:', error);
    }
  };
  
  const handlePostNow = async () => {
    const selectedPlatforms = Object.keys(platforms).filter(p => platforms[p]);
    
    if (selectedPlatforms.length === 0) {
      toast.error('Please select at least one platform');
      return;
    }
    
    if (!postForm.content.trim()) {
      toast.error('Please enter post content');
      return;
    }
    
    setLoading(true);
    try {
      const response = await socialAPI.postToSocial({
        content: postForm.content,
        platforms: selectedPlatforms,
        link: postForm.link || null,
        media_url: postForm.media_url || null
      });
      
      toast.success(`Posted to ${response.data.platforms_posted} platform(s)`);
      setPostForm({ content: '', link: '', media_url: '', schedule_time: '' });
      setPlatforms({ twitter: false, facebook: false, linkedin: false, instagram: false });
      loadPostHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to post');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSchedulePost = async () => {
    const selectedPlatforms = Object.keys(platforms).filter(p => platforms[p]);
    
    if (selectedPlatforms.length === 0) {
      toast.error('Please select at least one platform');
      return;
    }
    
    if (!postForm.content.trim()) {
      toast.error('Please enter post content');
      return;
    }
    
    if (!postForm.schedule_time) {
      toast.error('Please select schedule time');
      return;
    }
    
    setLoading(true);
    try {
      const response = await socialAPI.schedulePost({
        content: postForm.content,
        platforms: selectedPlatforms,
        link: postForm.link || null,
        media_url: postForm.media_url || null,
        schedule_time: postForm.schedule_time
      });
      
      toast.success('Post scheduled successfully');
      setPostForm({ content: '', link: '', media_url: '', schedule_time: '' });
      setPlatforms({ twitter: false, facebook: false, linkedin: false, instagram: false });
      loadScheduledPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule post');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCancelScheduled = async (postId) => {
    try {
      await socialAPI.cancelScheduledPost(postId);
      toast.success('Scheduled post cancelled');
      loadScheduledPosts();
    } catch (error) {
      toast.error('Failed to cancel post');
    }
  };
  
  const getPlatformIcon = (platform) => {
    switch (platform) {
      case 'twitter': return <Twitter className="h-4 w-4" />;
      case 'facebook': return <Facebook className="h-4 w-4" />;
      case 'linkedin': return <Linkedin className="h-4 w-4" />;
      case 'instagram': return <Instagram className="h-4 w-4" />;
      default: return null;
    }
  };
  
  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Social Media Manager</h1>
          <p className="text-gray-600 mt-2">Post and schedule content across multiple platforms</p>
        </div>
        
        <Tabs defaultValue="composer" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="composer">Composer</TabsTrigger>
            <TabsTrigger value="scheduled">Scheduled</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>
          
          {/* Composer Tab */}
          <TabsContent value="composer" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Create Post</CardTitle>
                <CardDescription>Write your content and select platforms</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Platform Selection */}
                <div>
                  <Label>Select Platforms</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
                    {Object.entries(platformStatus).map(([key, platform]) => (
                      <div key={key} className="flex items-center space-x-2 p-3 border rounded-lg">
                        <Checkbox
                          id={key}
                          checked={platforms[key]}
                          onCheckedChange={(checked) => setPlatforms({ ...platforms, [key]: checked })}
                          disabled={!platform.configured}
                        />
                        <label htmlFor={key} className="flex items-center gap-2 cursor-pointer">
                          {getPlatformIcon(key)}
                          <span className="text-sm">{platform.name}</span>
                          {!platform.configured && <span className="text-xs text-red-500">(Not configured)</span>}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Content */}
                <div>
                  <Label>Post Content *</Label>
                  <Textarea
                    placeholder="What's on your mind?"
                    value={postForm.content}
                    onChange={(e) => setPostForm({ ...postForm, content: e.target.value })}
                    rows={6}
                  />
                  <p className="text-xs text-gray-500 mt-1">{postForm.content.length} characters</p>
                </div>
                
                {/* Link */}
                <div>
                  <Label>Link (Optional)</Label>
                  <Input
                    type="url"
                    placeholder="https://example.com"
                    value={postForm.link}
                    onChange={(e) => setPostForm({ ...postForm, link: e.target.value })}
                  />
                </div>
                
                {/* Media URL */}
                <div>
                  <Label>Image URL (Optional)</Label>
                  <Input
                    type="url"
                    placeholder="https://example.com/image.jpg"
                    value={postForm.media_url}
                    onChange={(e) => setPostForm({ ...postForm, media_url: e.target.value })}
                  />
                </div>
                
                {/* Schedule Time */}
                <div>
                  <Label>Schedule Time (Optional)</Label>
                  <Input
                    type="datetime-local"
                    value={postForm.schedule_time}
                    onChange={(e) => setPostForm({ ...postForm, schedule_time: e.target.value })}
                  />
                </div>
                
                {/* Action Buttons */}
                <div className="flex gap-3">
                  <Button onClick={handlePostNow} disabled={loading} className="flex-1">
                    {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Posting...</> : <><Send className="mr-2 h-4 w-4" /> Post Now</>}
                  </Button>
                  {postForm.schedule_time && (
                    <Button onClick={handleSchedulePost} disabled={loading} variant="outline" className="flex-1">
                      <Calendar className="mr-2 h-4 w-4" /> Schedule Post
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Scheduled Tab */}
          <TabsContent value="scheduled" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Scheduled Posts</CardTitle>
                <CardDescription>Manage your upcoming scheduled posts</CardDescription>
              </CardHeader>
              <CardContent>
                {scheduledPosts.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No scheduled posts</p>
                ) : (
                  <div className="space-y-3">
                    {scheduledPosts.map((post) => (
                      <div key={post._id} className="p-4 border rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <p className="font-medium line-clamp-2">{post.content}</p>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleCancelScheduled(post._id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="h-4 w-4" />
                          {new Date(post.schedule_time).toLocaleString()}
                        </div>
                        <div className="flex gap-2 mt-2">
                          {post.platforms?.map((p) => (
                            <span key={p} className="flex items-center gap-1 text-xs bg-gray-100 px-2 py-1 rounded">
                              {getPlatformIcon(p)}
                              {p}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* History Tab */}
          <TabsContent value="history" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Post History</CardTitle>
                <CardDescription>View your previously posted content</CardDescription>
              </CardHeader>
              <CardContent>
                {postHistory.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No post history</p>
                ) : (
                  <div className="space-y-3">
                    {postHistory.map((post) => (
                      <div key={post._id} className="p-4 border rounded-lg">
                        <p className="font-medium line-clamp-2 mb-2">{post.content}</p>
                        <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                          <History className="h-4 w-4" />
                          {new Date(post.posted_at).toLocaleString()}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {post.results && Object.entries(post.results).map(([platform, result]) => (
                            <span
                              key={platform}
                              className={`flex items-center gap-1 text-xs px-2 py-1 rounded ${
                                result.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {getPlatformIcon(platform)}
                              {platform}
                              {result.success ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
};

export default SocialMediaManager;
